import React, { useEffect, useState, useCallback } from 'react';
import { observer } from 'mobx-react';
import {
  Button,
  PopupWindow,
  Table,
  MessagePopupCore,
} from '@utahdts/dts-react-common';
import Icon from '../misc/Icon';
import appStore from '../app/mobx/appStore';
import columns from './columns/ApprovalColumns';
import webservice from '../app/webservice/webservice';
import Approval from './modals/Approval';
import security from '../app/roles/security';
import roles from '../app/roles/roles';
import { sortByLastUpdatedDesc } from '../misc/Shared';

const Approvals = () => {
  const [isBusy, setBusy] = useState(true);
  const [approvals, setApprovals] = useState([]);
  const [showApproval, displayApproval] = useState(false);
  const [showDelete, displayDelete] = useState(false);
  const [approval, setApproval] = useState({});
  const [isNew, setIsNew] = useState(true);
  // Companies (with FEINs) for the Administrator "Company" selector on a new
  // history; empty for Org Administrators (they file against their current org).
  const [orgOptions, setOrgOptions] = useState([]);

  const displayError = useCallback((error) => {
    // Enhance logging to ensure something is always printed to the console.
    if (error) {
      console.error('An error occurred:', error);
    } else {
      console.warn(
        'displayError was called, but no error object was provided. This may indicate a promise was rejected without a value.',
      );
    }
    setBusy(false);
    MessagePopupCore.addMessage({
      title: 'Something went wrong',
      message: 'An error occurred. Please try again later.',
    });
  }, []);

  const getApprovals = useCallback(() => {
    console.log('[Approvals] getApprovals called');
    console.log('[Approvals] currentOrg:', appStore.currentOrg);
    console.log(
      '[Approvals] hasAnyRole:',
      security.hasAnyRole([roles.dev, roles.admin]),
    );

    const isPrivilegedUser = security.hasAnyRole([roles.dev, roles.admin]);
    const hasOrg = !!appStore.currentOrg;

    if (isPrivilegedUser || hasOrg) {
      setBusy(true);
      const orgId = appStore.currentOrg || '';
      console.log('[Approvals] Fetching approvals for org:', orgId);

      webservice.wac
        .drugApprovalPerOrg(orgId)
        .then((data) => {
          console.log('[Approvals] Received data from API:', data);
          // Ensure approvals is always an array to prevent crashes in the Table component.
          const approvalData = data.drugApprovalByOrg || [];
          console.log('[Approvals] Setting approvals:', approvalData);
          setApprovals(approvalData);
          setBusy(false);
        })
        .catch((error) => {
          console.error('[Approvals] Error fetching approvals:', error);
          displayError(error);
        });
    } else {
      console.log(
        '[Approvals] No org selected and user is not admin - clearing list',
      );
      // If no org is selected and user is not an admin, clear list and stop loading.
      setApprovals([]);
      setBusy(false);
    }
  }, [appStore.currentOrg, displayError]);

  useEffect(() => {
    getApprovals();
  }, [getApprovals]);

  // Administrators have no single selected org, so a new history must be
  // associated with a company they pick. Load the companies for the selector;
  // Org Administrators use their current org and get no list.
  const getOrgs = useCallback(() => {
    if (!security.hasAnyRole([roles.dev, roles.admin])) {
      setOrgOptions([]);
      return;
    }
    const result =
      webservice.company &&
      typeof webservice.company.all === 'function' &&
      webservice.company.all();
    if (result && typeof result.then === 'function') {
      result
        .then((data) => setOrgOptions(data?.orgs || []))
        .catch(() => setOrgOptions([]));
    }
  }, []);

  useEffect(() => {
    getOrgs();
  }, [getOrgs]);

  const editApproval = useCallback(
    (isNew, approvalToEdit, shouldShow) => {
      setIsNew(isNew);
      const orgID = security.hasAnyRole([roles.dev, roles.admin])
        ? ''
        : appStore.currentOrg;
      // Avoid mutating the argument, create a new object
      const newApproval = {
        ...approvalToEdit,
        org: approvalToEdit.org ?? orgID,
      };
      setApproval(newApproval);
      displayApproval(shouldShow);
    },
    [appStore.currentOrg],
  );

  const resetApproval = useCallback(() => {
    editApproval(true, {}, false);
    displayDelete(false);
  }, [editApproval]);

  const deleteApproval = useCallback((approvalToDelete) => {
    console.log(
      '[Delete Approval] Initiating delete for approval:',
      approvalToDelete,
    );
    console.log('[Delete Approval] Approval ID:', approvalToDelete?.id);
    setApproval(approvalToDelete);
    displayDelete(true);
  }, []);

  // Uses the 'approval' from state, which is set when the delete button in the row is clicked.
  const actualDelete = useCallback(() => {
    console.log(
      '[Delete Approval] Executing actual delete for approval:',
      approval,
    );
    console.log('[Delete Approval] Approval ID being deleted:', approval?.id);

    return webservice.wac
      .deleteApproval({ id: approval.id })
      .then((result) => {
        console.log('[Delete Approval] Delete successful, result:', result);
        resetApproval();
        getApprovals();
        return result;
      })
      .catch((error) => {
        console.error('[Delete Approval] Delete failed with error:', error);
        console.error('[Delete Approval] Error details:', {
          message: error.message,
          stack: error.stack,
          approvalId: approval?.id,
        });
        displayError(error);
      });
  }, [approval, getApprovals, resetApproval, displayError]);

  const saveApproval = useCallback(
    (approvalToSave) =>
      webservice.wac
        .saveApproval(approvalToSave)
        .then(() => {
          resetApproval();
          getApprovals();
        })
        .catch((error) => {
          displayError(error);
          // Re-throw the error so the modal can also catch it and stop its own loading spinner.
          throw error;
        }),
    [getApprovals, resetApproval, displayError],
  );

  return (
    <div key="listings" className="home-wrapper">
      <div className="home-content">
        <h1 className="mb-zero">Approval History</h1>
      </div>
      <div className="buttons flex">
        <Button
            label="Manually Enter History"
            className="mr-spacing flex align-items-center"
            onClick={() => editApproval(true, {}, true)}
            children={
                <span className="icon--round ml-spacing">{Icon.iconPencil()}</span>
            }
            childrenPostion={Button.CHILDREN_POSITION.AFTER}
        />
      </div>
      <div>
        <Table
          list={sortByLastUpdatedDesc(approvals)}
          columns={columns(editApproval, deleteApproval)}
          filters={true}
          showLimit={50}
          ajaxSpinner={isBusy}
        />
      </div>
      {showApproval && (
        <Approval
          onClose={resetApproval}
          onSave={saveApproval}
          isNew={isNew}
          approval={approval}
          orgs={orgOptions}
        />
      )}
      {showDelete && (
        <PopupWindow
          className="popup-window--large"
          onCloseButtonCallback={resetApproval}
          footerChildren={[
            <Button
              label="Cancel"
              onClick={resetApproval}
              key="close-button"
              className="button--black"
            />,
            <Button
              label="Delete"
              busy={isBusy}
              onClick={actualDelete}
              key="delete-button"
            />,
          ]}
        >
          <h3>Are you sure?</h3>
          <p>You are about to delete an approval.</p>
        </PopupWindow>
      )}
    </div>
  );
};

export default observer(Approvals);
